<template>
  <div class="w-full">
    <div v-if="showsSubscription" class="surface-0 px-3 md:px-4 py-3">
      <div class="site-list-tools w-full">
        <Filters
          v-if="hasListControls"
          embedded
          :filter_columns="doc.filters"
          :sort_columns="doc.sort_columns"
        />
        <div
          class="surface-50 border-1 border-100 border-round p-3 flex flex-column sm:flex-row gap-3 align-items-start sm:align-items-center justify-content-between"
        >
          <div class="flex align-items-center gap-3">
            <div
              class="surface-900 text-white border-circle flex align-items-center justify-content-center flex-shrink-0"
              style="width: 2.75rem; height: 2.75rem"
            >
              <i class="pi pi-envelope text-lg text-white" />
            </div>
            <div>
              <div class="font-semibold text-900 mb-1">Stay connected</div>
              <div class="text-sm text-600">
                Receive the latest {{ doc.name.toLowerCase() }} from SATS by
                email.
              </div>
            </div>
          </div>

          <NuxtLink
            :to="`/subscribe?content=${doc.name.toLowerCase()}`"
            class="no-underline w-full sm:w-auto"
          >
            <Button
              rounded
              label="Subscribe"
              icon="pi pi-arrow-right"
              iconPos="right"
              class="surface-900 text-white border-none"
            />
          </NuxtLink>
        </div>
      </div>
    </div>
    <Filters
      v-else-if="hasListControls"
      :filter_columns="doc.filters"
      :sort_columns="doc.sort_columns"
    />
    <CardSkeletons
      v-if="itemsPending"
      :count="config.queries[path]?.limit ?? 10"
      :item-class="`col-12 ${web_content_type.break_pt}:${web_content_type.width} p-1`"
      :show-media="web_content_type.show_img && path !== 'resources/courses'"
      :info-columns="doc.info_columns ?? []"
    />
    <template v-else-if="list && list?.length > 0">
      <div class="grid p-3 surface-100 w-full">
        <div
          v-for="item in list"
          :key="item.id"
          :class="`col-12 ${web_content_type.break_pt}:${web_content_type.width} p-1`"
        >
          <NuxtLink
            :to="item?.link"
            class="block no-underline text-color h-full"
          >
            <component
              :is="components[web_content_type?.web_component]"
              :item="item"
              :web_content_type="web_content_type"
            />
          </NuxtLink>
        </div>
      </div>
    </template>
    <template v-else>
      <SiteEmptyState
        compact
        live
        eyebrow="No results"
        title="No items found"
        description="We couldn't find any items to show. Try adjusting your search or filters, or check back again soon."
        icon="pi pi-search-minus"
      />
    </template>
    <Paginator
      v-if="config.queries[path] && doc.filters?.length > 0 && totalRecords > 0"
      v-model:first="config.queries[path].skip"
      v-model:rows="config.queries[path].limit"
      :totalRecords="totalRecords || 0"
      :rowsPerPageOptions="[10, 20, 30]"
    />
  </div>
</template>

<script setup>
import { hash } from 'ohash'
const config = useConfigStore()
const route = useRoute()

const props = defineProps({
  doc: {
    type: Object,
    required: true
  }
})

const SUBSCRIBABLE_CONTENT_NAMES = [
  'Blogs',
  'Live Events',
  'Newsletters',
  'Student Research'
]

const hasListControls = computed(
  () =>
    Boolean(props.doc.filters?.length) ||
    Boolean(props.doc.sort_columns?.length)
)
const showsSubscription = computed(() =>
  SUBSCRIBABLE_CONTENT_NAMES.includes(props.doc.name)
)

const web_content_type = ref({
  width: 'col-4',
  break_pt: 'xl',
  web_component: 'Card',
  body_summary: true,
  img_width: [4, 10, 17].includes(props.doc.object_type_id)
    ? 'contain'
    : 'cover',
  show_img: true,
  title_align: 'left',
  title_weight: 'semibold',
  title_size: 'lg',
  img_pos: 'top',
  height: 'full'
})

const path = ref([props.doc.path, props.doc.slug].filter(Boolean).join('/'))

if (!config.queries[path.value]) {
  config.queries[path.value] = JSON.parse(JSON.stringify(props.doc.query ?? {}))
}

config.queries[path.value].path = path.value
Object.assign(config.queries[path.value].where, {
  path: path.value
})

if (route.query) {
  Object.entries(route.query).forEach(([q, v]) => {
    const value = Number(v)
    config.queries[path.value].where[q] = isNaN(value) ? v : value
    Object.assign(config.queries[path.value].where, {
      [q]: value
    })
  })
}

// Keep this page's query stable while navigation removes it from the shared store.
const pageQuery = config.queries[path.value]
const contentVersion = useRuntimeConfig().public.content.integrity
const listQuery = computed(() => JSON.stringify(pageQuery))
const listKey = computed(() => `${contentVersion}-${hash(listQuery.value)}`)

const { data: landingPage, status: listStatus } = await useLazyAsyncData(
  () => `landing-page-${listKey.value}`,
  () => $fetch(`/api/landing-page/${listKey.value}`, { query: { query: listQuery.value } })
)

const list = computed(() => landingPage.value?.items ?? [])
const totalRecords = computed(() => landingPage.value?.total ?? 0)
const itemsPending = computed(() => ['idle', 'pending'].includes(listStatus.value))

watch(
  () => config.queries[path.value]?.skip,
  skip => {
    if (skip === undefined) return

    scrollToTop()
  }
)

watch(
  () => config.queries[path.value]?.limit,
  limit => {
    const query = config.queries[path.value]

    if (limit === undefined || !query) return

    query.skip = 0
    scrollToTop()
  }
)

const scrollToTop = () => {
  window.scrollTo({
    top: 0,
    behavior: 'smooth'
  })
}
</script>

<style lang="scss" scoped>
.site-list-tools {
  display: grid;
  gap: 0.75rem;
}
</style>
